import React, { useState, useEffect, useCallback } from "react";
import Editor from "@monaco-editor/react";
import { fetchData, putData } from "../../utils/api";
import { toast } from "react-toastify";

const VESTA_PRIMARY = "#633AFB";

const STAGE_TABS = [
  { id: "triage", label: "Stage 1: Triage", payloadKey: "triage_payload" },
  { id: "deep_estimator", label: "Stage 2: Deep Estimation", payloadKey: "deep_estimator_payload" },
];

const getPayloadFormat = (payloadString) => {
  try {
    const payload = JSON.parse(payloadString);
    if (payload.input || payload.text?.format) {
      return { format: "Responses API", color: "#059669", bg: "#d1fae5" };
    }
    if (payload.messages || payload.response_format) {
      return { format: "Chat Completions (legacy)", color: "#d97706", bg: "#fef3c7" };
    }
    return { format: "Unknown", color: "#6b7280", bg: "#f3f4f6" };
  } catch {
    return { format: "Invalid JSON", color: "#dc2626", bg: "#fef2f2" };
  }
};

const PayloadEditorModal = ({ show, onClose }) => {
  const [activeStage, setActiveStage] = useState("triage");
  const [payloads, setPayloads] = useState({ triage: "", deep_estimator: "" });
  const [originalPayloads, setOriginalPayloads] = useState({ triage: "", deep_estimator: "" });
  const [defaults, setDefaults] = useState({ triage: "", deep_estimator: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (show) {
      fetchPayloads();
    }
  }, [show]);

  const fetchPayloads = async () => {
    setLoading(true);
    try {
      const data = await fetchData("api/admin/attendee_value_payloads");

      const formatPayload = (raw) => {
        if (!raw) return "";
        try {
          const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
          return JSON.stringify(parsed, null, 2);
        } catch {
          return typeof raw === "string" ? raw : JSON.stringify(raw, null, 2);
        }
      };

      const newPayloads = {
        triage: formatPayload(data.triage_payload),
        deep_estimator: formatPayload(data.deep_estimator_payload),
      };
      const newDefaults = {
        triage: formatPayload(data.defaults?.triage_payload),
        deep_estimator: formatPayload(data.defaults?.deep_estimator_payload),
      };

      setPayloads(newPayloads);
      setOriginalPayloads(newPayloads);
      setDefaults(newDefaults);
      setValidationErrors({});
    } catch (err) {
      toast.error("Failed to load payloads");
    } finally {
      setLoading(false);
    }
  };

  const validateJSON = useCallback((value, stage) => {
    try {
      JSON.parse(value);
      setValidationErrors((prev) => ({ ...prev, [stage]: null }));
      return true;
    } catch (e) {
      setValidationErrors((prev) => ({ ...prev, [stage]: e.message }));
      return false;
    }
  }, []);

  const handleEditorChange = (value, stage) => {
    setPayloads((prev) => ({ ...prev, [stage]: value }));
    validateJSON(value, stage);
  };

  const handleSave = async () => {
    const allValid = STAGE_TABS.every((tab) => validateJSON(payloads[tab.id], tab.id));
    if (!allValid) {
      toast.error("Please fix JSON validation errors before saving.");
      return;
    }

    setSaving(true);
    try {
      await putData("api/admin/attendee_value_payloads", {
        triage_payload: payloads.triage,
        deep_estimator_payload: payloads.deep_estimator,
      });
      setOriginalPayloads({ ...payloads });
      toast.success("Payloads saved successfully!");
    } catch (err) {
      const message = err?.response?.data?.error || "Failed to save payloads";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetStage = () => {
    const defaultValue = defaults[activeStage];
    if (defaultValue) {
      setPayloads((prev) => ({ ...prev, [activeStage]: defaultValue }));
      setValidationErrors((prev) => ({ ...prev, [activeStage]: null }));
    }
  };

  const handleResetAll = () => {
    if (window.confirm("Reset all stages to default? This will discard any unsaved changes.")) {
      setPayloads({ ...defaults });
      setValidationErrors({});
    }
  };

  const hasUnsavedChanges = () => {
    return STAGE_TABS.some((tab) => payloads[tab.id] !== originalPayloads[tab.id]);
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      if (!window.confirm("You have unsaved changes. Discard them?")) return;
    }
    onClose();
  };

  if (!show) return null;

  return (
    <div
      className="modal d-block payload-editor-modal"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content" style={{ borderRadius: "12px", border: "none" }}>
          <div className="modal-header" style={{ background: VESTA_PRIMARY, color: "white", borderRadius: "12px 12px 0 0" }}>
            <h5 className="modal-title fw-bold text-white">Configure AI Payloads</h5>
            <button type="button" className="close-btn" style={{ color: "white" }} onClick={handleClose} aria-label="Close">×</button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" style={{ color: VESTA_PRIMARY }} />
                <p className="mt-2 text-muted">Loading payloads...</p>
              </div>
            ) : (
              <>
                {/* Info Banner */}
                <div
                  style={{
                    padding: "12px 16px",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: "6px",
                    marginBottom: "16px",
                    fontSize: "13px",
                    color: "#1e40af",
                  }}
                >
                  <strong>LLM Payload Configuration</strong>
                  <p style={{ margin: "8px 0 0 0" }}>
                    Customize the LLM payloads for attendee value estimation. Use{" "}
                    <code style={{ background: "#dbeafe", padding: "2px 4px", borderRadius: "3px" }}>{"{{EVENT_NAME}}"}</code>,{" "}
                    <code style={{ background: "#dbeafe", padding: "2px 4px", borderRadius: "3px" }}>{"{{DESCRIPTION}}"}</code>,{" "}
                    <code style={{ background: "#dbeafe", padding: "2px 4px", borderRadius: "3px" }}>{"{{TICKET_URL}}"}</code>{" "}
                    for dynamic values. Set to blank and save to reset to code defaults.
                  </p>
                </div>

                {/* Stage Tabs - same style as Social Media AI settings modal */}
                <div className="settings-tabs payload-editor-settings-tabs">
                  {STAGE_TABS.map((tab) => (
                    <button
                      type="button"
                      key={tab.id}
                      className={`settings-tab ${activeStage === tab.id ? "active" : ""}`}
                      onClick={() => setActiveStage(tab.id)}
                    >
                      {tab.label}
                      {validationErrors[tab.id] && (
                        <span className="badge bg-danger ms-2" style={{ fontSize: "10px" }}>!</span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Format Indicator */}
                <div className="d-flex justify-content-end mb-2">
                  {(() => {
                    const formatInfo = getPayloadFormat(payloads[activeStage]);
                    return (
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "12px",
                          fontSize: "11px",
                          fontWeight: "500",
                          background: formatInfo.bg,
                          color: formatInfo.color,
                        }}
                      >
                        Format: {formatInfo.format}
                      </span>
                    );
                  })()}
                </div>

                {/* Editor */}
                <div
                  style={{
                    border: validationErrors[activeStage]
                      ? "2px solid #dc2626"
                      : "1px solid #e5e7eb",
                    borderRadius: "6px",
                    overflow: "hidden",
                    marginBottom: "12px",
                  }}
                >
                  <Editor
                    height="400px"
                    language="json"
                    theme="vs-light"
                    value={payloads[activeStage]}
                    onChange={(value) => handleEditorChange(value, activeStage)}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      lineNumbers: "on",
                      scrollBeyondLastLine: false,
                      wordWrap: "on",
                      automaticLayout: true,
                      tabSize: 2,
                      formatOnPaste: true,
                    }}
                  />
                </div>

                {/* Validation Error */}
                {validationErrors[activeStage] && (
                  <div
                    className="alert alert-danger py-2"
                    style={{ fontSize: "13px" }}
                  >
                    <strong>JSON Error:</strong> {validationErrors[activeStage]}
                  </div>
                )}

                {/* Unsaved Changes Warning */}
                {hasUnsavedChanges() && (
                  <div style={{ fontSize: "12px", color: "#f59e0b", fontStyle: "italic", marginBottom: "8px" }}>
                    You have unsaved changes.
                  </div>
                )}
              </>
            )}
          </div>
          {!loading && (
            <div className="modal-footer d-flex justify-content-between">
              <div className="d-flex payload-editor-footer-left">
                <button className="btn btn-sm btn-outline-secondary" onClick={handleResetStage}>
                  Reset Stage to Default
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleResetAll}
                  style={{ color: "#6b7280" }}
                >
                  Reset All
                </button>
              </div>
              <div className="d-flex payload-editor-footer-right">
                <button className="btn btn-sm btn-outline-secondary" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: VESTA_PRIMARY, color: "white", border: "none" }}
                  onClick={handleSave}
                  disabled={saving || Object.values(validationErrors).some((e) => e)}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Saving...
                    </>
                  ) : (
                    "Save Settings"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayloadEditorModal;
